/**
 * EDI / RevQ demo API client — calls our backend proxy at /api/revqdemo/*,
 * which forwards to https://ehi.ariaone.ai (see /app/backend/revqdemo_routes.py).
 *
 * Used by /revqdemo/login only. The vendor's auth JWT is returned by
 * login() and signupFinalize(); store it however the consuming page sees
 * fit (this client does not touch localStorage).
 */
const BASE = process.env.REACT_APP_BACKEND_URL;

async function postJson(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON response */ }
  return { ok: res.ok, status: res.status, data };
}

async function getJson(path) {
  const res = await fetch(`${BASE}${path}`);
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON response */ }
  return { ok: res.ok, status: res.status, data };
}

/* ─── Use case 2: authenticate ─────────────────────────────────── */
export async function login({ email, password }) {
  return postJson('/api/revqdemo/login', { email, password });
}

/* ─── Use case 1: self-register tenant (Stripe $0 SetupIntent) ─── */
export async function signupIntent({ email, password, first_name, last_name, origin_url }) {
  return postJson('/api/revqdemo/signup/intent', {
    email, password, first_name, last_name, origin_url,
  });
}

export async function signupFinalize(session_id) {
  return postJson(`/api/revqdemo/signup/finalize/${encodeURIComponent(session_id)}`);
}

/* Public Stripe key for embedded checkout (fallback path — the
   publishable_key is normally returned inline by signupIntent). */
export async function billingConfig() {
  return getJson('/api/revqdemo/billing/config');
}

/* ─── Tenant-scoped reads (requires Authorization: Bearer <EDI_JWT>) ─── */
async function getJsonAuth(path, token) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON response */ }
  return { ok: res.ok, status: res.status, data };
}

export const getSubLogins = (token) => getJsonAuth('/api/revqdemo/sub-logins', token);
export const getSubLoginModules = (token) => getJsonAuth('/api/revqdemo/sub-logins/modules', token);
export const getClients   = (token) => getJsonAuth('/api/revqdemo/clients', token);
export const getProviders = (token) => getJsonAuth('/api/revqdemo/providers', token);

async function postJsonAuth(path, token, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON */ }
  return { ok: res.ok, status: res.status, data };
}

export const lookupNpi  = (token, npi_number) =>
  postJsonAuth('/api/revqdemo/providers/lookup', token, { npi_number });
export const addProvider = (token, payload) =>
  postJsonAuth('/api/revqdemo/providers', token, payload);
export const addClient = (token, payload) =>
  postJsonAuth('/api/revqdemo/clients', token, payload);
export const createSubLogin = (token, payload) =>
  postJsonAuth('/api/revqdemo/sub-logins', token, payload);
